if (!customElements.get("scrollable-group-pagination")) {
  class ScrollableGroupPagination extends HTMLElement {
    constructor() {
      super();
      this.scrollableGroup = null;
      this.dots = [];
      this.currentPage = 0;
    }

    setScrollableGroup(scrollableGroup) {
      // Clean up existing scroll handler if any
      if (
        this.scrollHandler &&
        this.scrollableGroup &&
        this.scrollableGroup.scrollableTrack
      ) {
        this.scrollableGroup.scrollableTrack.removeEventListener(
          "scroll",
          this.scrollHandler,
        );
      }

      this.scrollableGroup = scrollableGroup;

      // Small delay to ensure DOM is ready, especially on mobile
      setTimeout(() => {
        this.createPaginationDots();
        this.setupScrollListener();
        this.setupResizeListener();
      }, 50);
    }

    createPaginationDots() {
      if (!this.scrollableGroup) {
        return;
      }

      // Check if scrolling is enabled for current breakpoint using media query
      const shouldEnableScroll = this.scrollableGroup.getCurrentScrollState();
      
      if (!shouldEnableScroll) {
        // Hide pagination if scrolling is disabled
        this.innerHTML = "";
        this.dots = [];
        return;
      }

      // Get visible elements only
      const visibleElements = this.scrollableGroup.scrollableElements.filter(
        (element) => {
          const style = window.getComputedStyle(element);
          return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            style.opacity !== "0"
          );
        },
      );

      const totalVisibleElements = visibleElements.length;

      // Calculate total pages based on current viewport - safely check media query
      const blocksPerPage = this.scrollableGroup.xlMediaQuery && this.scrollableGroup.xlMediaQuery.matches
        ? this.scrollableGroup.blocksToShowDesktop
        : this.scrollableGroup.blocksToShow;
      const totalPages = Math.ceil(totalVisibleElements / blocksPerPage);

      // Clear existing dots
      this.innerHTML = "";
      this.dots = [];

      // Get pagination styling from data attributes
      const inactiveDotClasses = this.getPaginationDotClasses();
      const activeDotClasses = this.getPaginationActiveDotClasses();

      // Create dots
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement("button");
        
        // Apply only the inactive classes from data attributes (no hardcoded classes)
        if (inactiveDotClasses) {
          inactiveDotClasses.split(' ').forEach(cls => {
            if (cls.trim()) dot.classList.add(cls.trim());
          });
        }
        
        dot.setAttribute("data-page", i);
        dot.setAttribute("aria-label", `Go to page ${i + 1}`);

        // Add click handler
        dot.addEventListener("click", () => this.scrollToPage(i));

        this.appendChild(dot);
        this.dots.push(dot);
      }
      
      // Store active and inactive dot classes for later use
      this.activeDotClasses = activeDotClasses;
      this.inactiveDotClasses = inactiveDotClasses;
      this.updateActiveDot(0);
    }

    scrollToPage(pageIndex) {
      if (!this.scrollableGroup || !this.scrollableGroup.scrollableTrack)
        return;

      const track = this.scrollableGroup.scrollableTrack;

      // Get visible elements only
      const visibleElements = this.scrollableGroup.scrollableElements.filter(
        (element) => {
          const style = window.getComputedStyle(element);
          return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            style.opacity !== "0"
          );
        },
      );

      // Determine blocks per page based on screen size using media query - safely check
      const blocksPerPage = this.scrollableGroup.xlMediaQuery && this.scrollableGroup.xlMediaQuery.matches
        ? this.scrollableGroup.blocksToShowDesktop
        : this.scrollableGroup.blocksToShow;

      // Calculate which visible element to scroll to
      const targetVisibleElementIndex = pageIndex * blocksPerPage;
      const targetVisibleElement = visibleElements[targetVisibleElementIndex];

      if (targetVisibleElement) {
        // Scroll to the target element
        targetVisibleElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    }

    setupScrollListener() {
      if (!this.scrollableGroup || !this.scrollableGroup.scrollableTrack)
        return;

      const track = this.scrollableGroup.scrollableTrack;

      // Listen to scroll events to detect which page's first element is at the leftmost edge
      const handleScroll = () => {
        const scrollLeft = track.scrollLeft;
        const trackWidth = track.clientWidth;
        const maxScrollLeft = track.scrollWidth - trackWidth;

        // Get visible elements only
        const visibleElements = this.scrollableGroup.scrollableElements.filter(
          (element) => {
            const style = window.getComputedStyle(element);
            return (
              style.display !== "none" &&
              style.visibility !== "hidden" &&
              style.opacity !== "0"
            );
          },
        );

        // Determine blocks per page based on screen size using media query - safely check
        const blocksPerPage = this.scrollableGroup.xlMediaQuery && this.scrollableGroup.xlMediaQuery.matches
          ? this.scrollableGroup.blocksToShowDesktop
          : this.scrollableGroup.blocksToShow;
        const totalPages = Math.ceil(visibleElements.length / blocksPerPage);

        // If we're at the very end, activate the last page
        if (scrollLeft >= maxScrollLeft) {
          this.updateActiveDot(totalPages - 1);
          return;
        }

        // Calculate which page we're on based on scroll position
        // Each page takes up the full track width
        const currentPage = Math.round(scrollLeft / trackWidth);

        // Ensure we don't go beyond the last page
        const clampedPage = Math.max(0, Math.min(currentPage, totalPages - 1));

        this.updateActiveDot(clampedPage);
      };

      // Add scroll listener
      track.addEventListener("scroll", handleScroll);

      // Store the handler for cleanup
      this.scrollHandler = handleScroll;

      // Initial call to set the correct active dot
      handleScroll();
    }

    updateActiveDot(pageIndex) {
      this.dots.forEach((dot, index) => {
        if (index === pageIndex) {
          // Remove inactive background classes
          if (this.inactiveDotClasses) {
            this.inactiveDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.remove(cls.trim());
            });
          }
          
          // Add active background classes
          if (this.activeDotClasses) {
            this.activeDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.add(cls.trim());
            });
          }
        } else {
          // Remove active background classes
          if (this.activeDotClasses) {
            this.activeDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.remove(cls.trim());
            });
          }
          
          // Add inactive background classes
          if (this.inactiveDotClasses) {
            this.inactiveDotClasses.split(' ').forEach(cls => {
              if (cls.trim() && cls.includes('bg-')) dot.classList.add(cls.trim());
            });
          }
        }
      });
      this.currentPage = pageIndex;
    }

    getPaginationDotClasses() {
      return this.dataset.paginationDotClasses || '';
    }

    getPaginationActiveDotClasses() {
      return this.dataset.paginationActiveDotClasses || '';
    }

    getArrowClasses() {
      return this.dataset.arrowClasses || '';
    }

    getArrowSvg() {
      const encodedSvg = this.dataset.arrowSvg || '';
      if (!encodedSvg) return '';
      
      // Decode HTML entities by creating a temporary element
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = encodedSvg;
      return tempDiv.innerHTML;
    }

    getArrowSvgMarkup(direction) {
      const customSvg = this.getArrowSvg();
      
      if (customSvg) {
        // If custom SVG is provided, use it and add rotate-180 class for left arrow
        if (direction === 'prev') {
          return customSvg.replace('<svg', '<svg class="rotate-180"');
        }
        return customSvg;
      }
      
      // Fallback to default chevron
      const svgSize = '16';
      const svgViewBox = '0 0 16 16';
      const path = 'M6 4L10 8L6 12';
      
      let svgClass = '';
      if (direction === 'prev') {
        svgClass = ' class="rotate-180"';
      }
      
      return `<svg${svgClass} width="${svgSize}" height="${svgSize}" viewBox="${svgViewBox}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="${path}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }

    setupResizeListener() {
      // Debounce resize events
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          // Force recalculation of pagination dots
          this.createPaginationDots();
          this.setupScrollListener();
        }, 100);
      };

      window.addEventListener("resize", handleResize);

      // Store the event listener for cleanup
      this.resizeHandler = handleResize;
    }



    disconnectedCallback() {
      if (
        this.scrollHandler &&
        this.scrollableGroup &&
        this.scrollableGroup.scrollableTrack
      ) {
        this.scrollableGroup.scrollableTrack.removeEventListener(
          "scroll",
          this.scrollHandler,
        );
      }
      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
      }
    }
  }

  customElements.define(
    "scrollable-group-pagination",
    ScrollableGroupPagination,
  );
}

if (!customElements.get("scrollable-group-controls")) {
  class ScrollableGroupControls extends HTMLElement {
    constructor() {
      super();
      this.scrollableGroup = null;
      this.prevButton = null;
      this.nextButton = null;
    }

    setScrollableGroup(scrollableGroup) {
      this.scrollableGroup = scrollableGroup;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.createControls();
        this.setupScrollListeners();
      }, 50);
    }

    createControls() {
      if (!this.scrollableGroup) {
        return;
      }

      // Check if custom arrows are already provided
      if (this.hasAttribute('data-custom-arrows') && this.children.length > 0) {
        // Custom arrows are already rendered, just add event listeners
        this.setupCustomArrowListeners();
        return;
      }

      // Clear existing content
      this.innerHTML = "";

      // Get arrow styling from data attributes
      const arrowClasses = this.getArrowClasses();

      // Create previous button
      this.prevButton = document.createElement("button");
      this.prevButton.innerHTML = this.getArrowSvgMarkup('prev');
      
      // Apply base classes
      this.prevButton.classList.add(
        "absolute",
        "left-4",
        "top-1/2",
        "-translate-y-1/2",
        "z-10",
        "flex",
        "items-center",
        "justify-center",
        "transition-all",
        "duration-200",
        "disabled:opacity-50",
        "disabled:cursor-not-allowed"
      );
      
      // Apply custom classes from data attributes
      if (arrowClasses) {
        arrowClasses.split(' ').forEach(cls => {
          if (cls.trim()) this.prevButton.classList.add(cls.trim());
        });
      }
      
      this.prevButton.setAttribute("aria-label", "Previous slide");
      this.prevButton.addEventListener("click", () => this.scrollPrev());

      // Create next button
      this.nextButton = document.createElement("button");
      this.nextButton.innerHTML = this.getArrowSvgMarkup('next');
      
      // Apply base classes
      this.nextButton.classList.add(
        "absolute",
        "right-4",
        "top-1/2",
        "-translate-y-1/2",
        "z-10",
        "flex",
        "items-center",
        "justify-center",
        "transition-all",
        "duration-200",
        "disabled:opacity-50",
        "disabled:cursor-not-allowed"
      );
      
      // Apply custom classes from data attributes
      if (arrowClasses) {
        arrowClasses.split(' ').forEach(cls => {
          if (cls.trim()) this.nextButton.classList.add(cls.trim());
        });
      }
      
      this.nextButton.setAttribute("aria-label", "Next slide");
      this.nextButton.addEventListener("click", () => this.scrollNext());

      this.appendChild(this.prevButton);
      this.appendChild(this.nextButton);
    }

    scrollPrev() {
      if (!this.scrollableGroup || !this.scrollableGroup.scrollableTrack) {
        return;
      }
      
      const track = this.scrollableGroup.scrollableTrack;
      const trackWidth = track.clientWidth;
      const newScrollLeft = track.scrollLeft - trackWidth;
      
      track.scrollTo({
        left: Math.max(0, newScrollLeft),
        behavior: 'smooth'
      });
    }

    scrollNext() {
      if (!this.scrollableGroup || !this.scrollableGroup.scrollableTrack) {
        return;
      }
      
      const track = this.scrollableGroup.scrollableTrack;
      const trackWidth = track.clientWidth;
      const maxScrollLeft = track.scrollWidth - trackWidth;
      const newScrollLeft = track.scrollLeft + trackWidth;
      
      track.scrollTo({
        left: Math.min(maxScrollLeft, newScrollLeft),
        behavior: 'smooth'
      });
    }

    setupCustomArrowListeners() {
      if (!this.scrollableGroup || !this.scrollableGroup.scrollableTrack) {
        return;
      }

      // Find custom arrow buttons
      const prevButton = this.querySelector('[data-direction="prev"], [data-scroll-previous]');
      const nextButton = this.querySelector('[data-direction="next"], [data-scroll-next]');

      if (prevButton) {
        prevButton.addEventListener("click", () => this.scrollPrev());
        this.prevButton = prevButton;
      }

      if (nextButton) {
        nextButton.addEventListener("click", () => this.scrollNext());
        this.nextButton = nextButton;
      }

      // Set up scroll listeners for custom arrows
      this.setupScrollListeners();
    }

    setupScrollListeners() {
      if (!this.scrollableGroup || !this.scrollableGroup.scrollableTrack) {
        return;
      }

      const track = this.scrollableGroup.scrollableTrack;

      // Listen to scroll events to update button states
      const handleScroll = () => {
        this.updateButtonStates();
      };

      track.addEventListener("scroll", handleScroll);
      
      // Store the handler for cleanup
      this.scrollHandler = handleScroll;
      
      // Initial call to set the correct button states
      handleScroll();
    }

    updateButtonStates() {
      if (!this.scrollableGroup || !this.scrollableGroup.scrollableTrack) {
        return;
      }

      const track = this.scrollableGroup.scrollableTrack;
      const scrollLeft = track.scrollLeft;
      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      
      // Handle custom arrows
      if (this.hasAttribute('data-custom-arrows')) {
        const prevButton = this.querySelector('[data-direction="prev"], [data-scroll-previous]');
        const nextButton = this.querySelector('[data-direction="next"], [data-scroll-next]');
        
        if (prevButton) {
          prevButton.disabled = scrollLeft <= 0;
        }
        if (nextButton) {
          nextButton.disabled = scrollLeft >= maxScrollLeft;
        }
        return;
      }

      // Handle default arrows
      if (this.prevButton && this.nextButton) {
        this.prevButton.disabled = scrollLeft <= 0;
        this.nextButton.disabled = scrollLeft >= maxScrollLeft;
      }
    }

    disconnectedCallback() {
      if (this.scrollHandler && this.scrollableGroup && this.scrollableGroup.scrollableTrack) {
        this.scrollableGroup.scrollableTrack.removeEventListener("scroll", this.scrollHandler);
      }
    }
  }

  customElements.define("scrollable-group-controls", ScrollableGroupControls);
}

if (!customElements.get("scrollable-group")) {
  class ScrollableGroup extends HTMLElement {
    constructor() {
      super();
      this.scrollableTrack = null;
      this.scrollableElements = [];
      this.blocksToShow = 1;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.isInitialized = false;

      // Drag scrolling properties
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragStartScrollLeft = 0;
      this.dragDistance = 0;
      this.dragThreshold = 5; // Minimum distance to start drag
      this.isClick = true; // Track if this should be a click event
      this.clickTarget = null; // Store the element that was clicked
      this.dragEventListeners = [];
      this.dragScrollingDisabled = false;
    }

    connectedCallback() {
      // Use Intersection Observer for lazy initialization
      // Only initialize when visible or within 50% of viewport
      this.setupLazyInitialization();
    }

    setupLazyInitialization() {
      // Check if element is already visible or near viewport
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const threshold = viewportHeight * 0.5; // 50% of viewport
      
      const isVisible = rect.top < viewportHeight + threshold && rect.bottom > -threshold;
      
      if (isVisible) {
        // Initialize immediately if visible or near viewport
      this.initializeScrollable();
      } else {
        // Use Intersection Observer for lazy initialization
        if (!this.intersectionObserver) {
          this.intersectionObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.initializeScrollable();
                  // Unobserve after initialization (only happens once)
                  this.intersectionObserver.unobserve(entry.target);
                }
              });
            },
            {
              root: null,
              rootMargin: '50% 0px 50% 0px', // 50% margin on top and bottom
              threshold: 0
            }
          );
        }
        this.intersectionObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      // Clean up media query listeners
      if (this.xlMediaQuery) {
        this.xlMediaQuery.removeEventListener("change", this.handleBreakpointChange.bind(this));
      }
      if (this.pointerMediaQuery) {
        this.pointerMediaQuery.removeEventListener("change", this.handleBreakpointChange.bind(this));
      }

      // Clean up drag event listeners
      this.removeDragEventListeners();
    }

    initializeScrollable() {
      // Prevent double initialization
      if (this.isInitialized) {
        return;
      }
      this.isInitialized = true;

      // Get the blocks to show settings
      this.blocksToShow = parseInt(this.dataset.blocksToShow) || 1;
      this.blocksToShowDesktop =
        parseInt(this.dataset.blocksToShowDesktop) || 4;

      // Get the enable scroll settings
      this.enableScroll = this.dataset.enableScroll === "true";
      this.enableScrollDesktop = this.dataset.enableScrollDesktop === "true";

      // Find the scrollable track element
      this.scrollableTrack = this.querySelector("scrollable-group-track");

      if (!this.scrollableTrack) {
        return;
      }

      // Get all direct children of the track
      this.scrollableElements = Array.from(this.scrollableTrack.children);

      if (this.scrollableElements.length === 0) {
        return;
      }

      // Skip initialization if there's only one child, but set col-width to 100%
      if (this.scrollableElements.length === 1) {
        this.scrollableTrack.style.setProperty('--col-width', '100%');
        return;
      }

      // Set up scroll snapping based on number of children
      this.setupScrollSnapping();

      // Set up resize observer to recalculate on size changes
      this.setupResizeObserver();

      // Set up responsive scroll handling first
      this.setupResponsiveScrollHandling();

      // Initialize pagination (after responsive handling to ensure media query is available)
      this.initializePagination();

      // Initialize controls (after responsive handling to ensure media query is available)
      this.initializeControls();

      // Set initial visibility for pagination and arrows
      this.updatePaginationVisibility();
      this.updateArrowsVisibility();

      // Set initial cursor state based on current breakpoint
      this.setInitialCursorState();

      // Drag scrolling is now handled by handleBreakpointChange() after responsive setup
      

    }

    // Method to get current scroll state based on media query
    getCurrentScrollState() {
      // If media query is not yet initialized, we need to check the current viewport width manually
      if (!this.xlMediaQuery) {
        const isDesktop = window.innerWidth >= 1280;
        const result = isDesktop ? this.enableScrollDesktop : this.enableScroll;
        
        return result;
      }
      
      const isDesktop = this.xlMediaQuery.matches;
      const isCoarsePointer = this.pointerMediaQuery && this.pointerMediaQuery.matches;
      
      // For pagination purposes, we want to know if scrolling is conceptually enabled
      // (regardless of pointer type), but for drag functionality, we need to check pointer type
      const result = isDesktop ? this.enableScrollDesktop : this.enableScroll;
      
      return result;
    }

    // Method to force refresh scroll state (useful for external calls)
    refreshScrollState() {
      if (this.xlMediaQuery) {
        this.handleBreakpointChange(this.xlMediaQuery);
      }
    }

    // Method to check if drag scrolling should be enabled (considers both scroll settings and pointer type)
    shouldEnableDragScrolling() {
      const isDesktop = this.xlMediaQuery && this.xlMediaQuery.matches;
      const isCoarsePointer = this.pointerMediaQuery && this.pointerMediaQuery.matches;
      
      // Enable drag scrolling only if scrolling is enabled AND we don't have a coarse pointer (touch device)
      return (isDesktop ? this.enableScrollDesktop : this.enableScroll) && !isCoarsePointer;
    }

    // Set initial cursor state based on current breakpoint and scroll settings
    setInitialCursorState() {
      if (!this.scrollableTrack) return;
      
      const shouldEnableDrag = this.shouldEnableDragScrolling();
      
      if (shouldEnableDrag) {
        this.scrollableTrack.classList.remove("cursor-default");
        this.scrollableTrack.classList.add("cursor-grab");
      } else {
        this.scrollableTrack.classList.remove("cursor-grab", "cursor-grabbing");
        this.scrollableTrack.classList.add("cursor-default");
      }
    }

    // Clean up any existing drag state and ensure cursor is correct
    cleanupDragState() {
      if (!this.scrollableTrack) return;
      
      // Reset drag state variables
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragStartScrollLeft = 0;
      this.dragDistance = 0;
      this.isClick = true;
      this.clickTarget = null;
      
      // Remove dragging class
      this.scrollableTrack.classList.remove("dragging");
      
      // Remove any drag-related cursor classes
      this.scrollableTrack.classList.remove("cursor-grab", "cursor-grabbing");
      
      // Add default cursor
      this.scrollableTrack.classList.add("cursor-default");
    }







    initializePagination() {
      const paginationElement = this.querySelector(
        "scrollable-group-pagination",
      );
      if (paginationElement && paginationElement.setScrollableGroup) {
        paginationElement.setScrollableGroup(this);
      }
    }

    initializeControls() {
      const controlsElement = this.querySelector(
        "scrollable-group-controls",
      );
      if (controlsElement && controlsElement.setScrollableGroup) {
        controlsElement.setScrollableGroup(this);
      }
    }

    // Dynamically show/hide pagination based on settings
    updatePaginationVisibility() {
      const paginationElement = this.querySelector("scrollable-group-pagination");
      if (!paginationElement) return;

      // Check if pagination should be visible based on Liquid block settings
      const isDesktop = this.xlMediaQuery && this.xlMediaQuery.matches;
      
      // Check if pagination is hidden by Liquid block settings
      const isHiddenByLiquid = paginationElement.classList.contains("max-md:hidden") && !isDesktop;
      const isHiddenByLiquidDesktop = paginationElement.classList.contains("md:hidden") && isDesktop;
      
      // Show pagination if not hidden by Liquid settings and scrolling is enabled
      const shouldShow = this.getCurrentScrollState() && !isHiddenByLiquid && !isHiddenByLiquidDesktop;
      
      if (shouldShow) {
        paginationElement.classList.remove("hidden");
        paginationElement.classList.add("flex");
      } else {
        paginationElement.classList.remove("flex");
        paginationElement.classList.add("hidden");
      }
    }

    // Dynamically show/hide arrows based on settings
    updateArrowsVisibility() {
      const arrowsElement = this.querySelector("scrollable-group-controls");
      if (!arrowsElement) return;

      // Check if arrows should be visible based on Liquid block settings
      const isDesktop = this.xlMediaQuery && this.xlMediaQuery.matches;
      
      // Check if arrows are hidden by Liquid block settings
      const isHiddenByLiquid = arrowsElement.classList.contains("max-md:hidden") && !isDesktop;
      const isHiddenByLiquidDesktop = arrowsElement.classList.contains("md:hidden") && isDesktop;
      
      // Show arrows if not hidden by Liquid settings and scrolling is enabled
      const shouldShow = this.getCurrentScrollState() && !isHiddenByLiquid && !isHiddenByLiquidDesktop;
      
      if (shouldShow) {
        arrowsElement.classList.remove("hidden");
        arrowsElement.classList.add("flex");
      } else {
        arrowsElement.classList.remove("flex");
        arrowsElement.classList.add("hidden");
      }
    }



    addDragEventListeners() {
      // Double-check that drag scrolling should be enabled before adding listeners
      // This considers both scroll settings AND pointer type (no drag on touch devices)
      const shouldEnableDrag = this.shouldEnableDragScrolling();
      if (!shouldEnableDrag) {
        return;
      }

      const track = this.scrollableTrack;

      // Mouse events
      const mouseDownHandler = (e) => this.handleDragStart(e);
      const mouseMoveHandler = (e) => this.handleDragMove(e);
      const mouseUpHandler = (e) => this.handleDragEnd(e);

      // Touch events
      const touchStartHandler = (e) => this.handleDragStart(e);
      const touchMoveHandler = (e) => this.handleDragMove(e);
      const touchEndHandler = (e) => this.handleDragEnd(e);

      // Prevent context menu during drag
      const contextMenuHandler = (e) => {
        if (this.isDragging) {
          e.preventDefault();
        }
      };

      // Prevent click events on children when dragging has occurred
      const clickHandler = (e) => {
        if (this.isDragging || this.dragDistance > this.dragThreshold) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return false;
        }
      };

      // Store listeners for cleanup
      this.dragEventListeners = [
        { element: track, event: "mousedown", handler: mouseDownHandler },
        { element: document, event: "mousemove", handler: mouseMoveHandler },
        { element: document, event: "mouseup", handler: mouseUpHandler },
        {
          element: track,
          event: "touchstart",
          handler: touchStartHandler,
          options: { passive: false },
        },
        {
          element: document,
          event: "touchmove",
          handler: touchMoveHandler,
          options: { passive: false },
        },
        { element: document, event: "touchend", handler: touchEndHandler },
        { element: track, event: "contextmenu", handler: contextMenuHandler },
        {
          element: track,
          event: "click",
          handler: clickHandler,
          options: { capture: true },
        },
      ];

      // Add all listeners
      this.dragEventListeners.forEach(
        ({ element, event, handler, options }) => {
          element.addEventListener(event, handler, options);
        },
      );
    }

    removeDragEventListeners() {
      this.dragEventListeners.forEach(
        ({ element, event, handler, options }) => {
          element.removeEventListener(event, handler, options);
        },
      );
      this.dragEventListeners = [];
      
      // Clean up cursor classes when removing drag listeners
      if (this.scrollableTrack) {
        this.scrollableTrack.classList.remove("cursor-grab", "cursor-grabbing");
        this.scrollableTrack.classList.add("cursor-default");
      }
    }

    handleDragStart(e) {
      // Check if drag scrolling should be enabled before allowing drag to start
      // This considers both scroll settings AND pointer type (no drag on touch devices)
      const shouldEnableDrag = this.shouldEnableDragScrolling();
      if (!shouldEnableDrag) {
        return;
      }

      // Prevent default behavior for touch events to avoid scrolling
      if (e.type === "touchstart") {
        e.preventDefault();
      }

      const point = this.getEventPoint(e);
      this.dragStartX = point.x;
      this.dragStartY = point.y;
      this.dragStartScrollLeft = this.scrollableTrack.scrollLeft;
      this.dragDistance = 0;
      this.isClick = true;
      this.clickTarget = e.target;

      // Store the original cursor classes
      this.originalCursorClasses = Array.from(this.scrollableTrack.classList).filter(cls => cls.startsWith('cursor-'));
      
      // Remove any existing cursor classes and add grabbing
      this.scrollableTrack.classList.remove(...this.originalCursorClasses);
      this.scrollableTrack.classList.add("cursor-grabbing");

      // Add dragging class for potential CSS styling
      this.scrollableTrack.classList.add("dragging");
    }

    handleDragMove(e) {
      if (!this.dragStartX && !this.dragStartY) return;

      const point = this.getEventPoint(e);
      const deltaX = point.x - this.dragStartX;
      const deltaY = point.y - this.dragStartY;

      // Calculate total distance moved
      this.dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Check if we've moved enough to start dragging
      if (this.dragDistance > this.dragThreshold) {
        this.isDragging = true;
        this.isClick = false;

        // Prevent text selection during drag
        e.preventDefault();

        // Calculate new scroll position with 1:1 ratio
        const newScrollLeft = this.dragStartScrollLeft - deltaX;

        // Apply scroll with bounds checking
        const maxScrollLeft =
          this.scrollableTrack.scrollWidth - this.scrollableTrack.clientWidth;
        this.scrollableTrack.scrollLeft = Math.max(
          0,
          Math.min(newScrollLeft, maxScrollLeft),
        );
      }
    }

    handleDragEnd(e) {
      // Use a small delay to ensure click events are handled before resetting drag state
      setTimeout(() => {
        // Reset drag state
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragStartScrollLeft = 0;
        this.dragDistance = 0;
        this.isClick = true;
        this.clickTarget = null;
      }, 10);

      // Restore cursor classes immediately
      this.scrollableTrack.classList.remove("cursor-grabbing");
      if (this.originalCursorClasses && this.originalCursorClasses.length > 0) {
        this.scrollableTrack.classList.add(...this.originalCursorClasses);
      } else {
        // Check if scrolling is enabled before setting cursor
        const shouldEnableScroll = this.getCurrentScrollState();
        if (shouldEnableScroll) {
          this.scrollableTrack.classList.add("cursor-grab");
        } else {
          this.scrollableTrack.classList.add("cursor-default");
        }
      }

      // Remove dragging class immediately
      this.scrollableTrack.classList.remove("dragging");
    }

    getEventPoint(e) {
      if (e.type.startsWith("touch")) {
        return {
          x: e.touches[0]?.clientX || e.changedTouches[0]?.clientX || 0,
          y: e.touches[0]?.clientY || e.changedTouches[0]?.clientY || 0,
        };
      } else {
        return {
          x: e.clientX,
          y: e.clientY,
        };
      }
    }

    setupScrollSnapping() {
      if (this.scrollableElements.length === 1) {
        // Single child: apply scroll styles directly to the child
        this.setupSingleChildSnapping();
      } else {
        // Multiple children: wrap in a container div and apply scroll styles to it
        this.setupMultipleChildrenSnapping();
      }
    }

    setupSingleChildSnapping() {
      const child = this.scrollableElements[0];

      // Get all classes from the single child
      const childClasses = Array.from(child.classList);

      // Process child classes to handle container and padding replacements
      const processedClasses = childClasses
        .map((className) => {
          // Replace container classes with scroll-px-container
          if (className === "container") {
            return "scroll-px-container";
          }
          if (className === "md:container") {
            return "md:scroll-px-container";
          }

          // Check if regular container class is found (not md:container)
          const hasRegularContainer = childClasses.includes("container");

          if (hasRegularContainer) {
            // Remove padding classes when regular container is present
            if (className.match(/^(p|px|pl|pr)-/)) {
              return null; // Remove this class
            }
          } else {
            // Add scroll-padding classes alongside original padding when no regular container
            if (className.match(/^(p|px|pl|pr)-/)) {
              const scrollClass = className.replace(
                /^(p|px|pl|pr)-/,
                "scroll-$1-",
              );
              return [className, scrollClass]; // Return both original and scroll class
            }
          }

          return className;
        })
        .flat()
        .filter(Boolean); // Flatten arrays and remove null values

      // Apply the processed child's classes to the track
      this.scrollableTrack.classList.add(...processedClasses);

      // Extract parent gap values and set parent gap variables for child calculations
      const spacingMap = {
        0: "0px",
        1: "4px",
        2: "8px",
        3: "12px",
        4: "16px",
        5: "20px",
        6: "24px",
        8: "32px",
        10: "40px",
        12: "48px",
        16: "64px",
        20: "80px",
        24: "96px",
        32: "128px",
      };

      // Find gap classes and extract values
      let parentGapStyle = "16px"; // Default fallback
      let xlGapStyle = "16px"; // Default fallback

      const classList = Array.from(this.classList);
      const gapClass = classList.find(
        (cls) => cls.startsWith("gap-") && !cls.startsWith("md:gap-"),
      );
      const xlGapClass = classList.find((cls) => cls.startsWith("md:gap-"));

      if (gapClass) {
        const gapValue = gapClass.replace("gap-", "");
        parentGapStyle = spacingMap[gapValue] || "16px";
      }

      if (xlGapClass) {
        const gapValue = xlGapClass.replace("md:gap-", "");
        xlGapStyle = spacingMap[gapValue] || parentGapStyle;
      } else {
        xlGapStyle = parentGapStyle; // Use mobile gap for desktop if no md: gap
      }

      this.scrollableTrack.style.setProperty("--parent-gap", parentGapStyle);
      this.scrollableTrack.style.setProperty("--xl-parent-gap", xlGapStyle);

      // Check if the single child actually contains other elements (is a container)
      // Only apply special unwrapping logic if it has children
      if (child.children.length > 0) {
        // Move all children of the single child into the track
        while (child.firstChild) {
          this.scrollableTrack.appendChild(child.firstChild);
        }

        // Remove the original single child
        child.remove();

        // Update the scrollable elements reference to the new children in the track
        this.scrollableElements = Array.from(this.scrollableTrack.children);

        // Now that we have multiple children in the track, use the multiple children setup
        this.setupMultipleChildrenSnapping();
      } else {
        // Single child has no children (e.g., single image) - keep it as is
        // Just apply scroll-snap-start to the single child
        child.classList.add("scroll-snap-start");
        
        // Keep the single child in the track
        this.scrollableElements = [child];
      }
    }

    setupMultipleChildrenSnapping() {
      // Apply scroll-snap-start to each child for proper scroll snapping
      this.scrollableElements.forEach((child, index) => {
        child.classList.add("scroll-snap-start");
      });
    }

    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        // No need to recalculate since CSS Grid handles sizing via CSS custom properties
        // The Liquid template already sets up the responsive auto-cols
      });

      // Observe the track for any potential future functionality
      this.resizeObserver.observe(this.scrollableTrack);
    }

    setupResponsiveScrollHandling() {
      // Set up media query listener for xl breakpoint (1280px)
      this.xlMediaQuery = window.matchMedia("(min-width: 1280px)");
      
      // Set up media query listener for pointer type (coarse = touch devices)
      this.pointerMediaQuery = window.matchMedia("(pointer: coarse)");
      
      // Initial call to set scroll state
      this.handleBreakpointChange(this.xlMediaQuery);
      
      // Listen for breakpoint changes
      this.xlMediaQuery.addEventListener("change", this.handleBreakpointChange.bind(this));
      
      // Listen for pointer type changes
      this.pointerMediaQuery.addEventListener("change", this.handleBreakpointChange.bind(this));
    }

        handleBreakpointChange(mediaQuery) {
      const isDesktop = this.xlMediaQuery && this.xlMediaQuery.matches;
      const isCoarsePointer = this.pointerMediaQuery && this.pointerMediaQuery.matches;
      
      // Enable scrolling if the setting is enabled AND we don't have a coarse pointer (touch device)
      const shouldEnableScroll = (isDesktop ? this.enableScrollDesktop : this.enableScroll) && !isCoarsePointer;
      
      if (this.scrollableTrack) {
        // Always start by cleaning up any existing drag state
        this.cleanupDragState();
        
        if (shouldEnableScroll) {
          // Enable scrolling - styles are handled by Liquid template
          // Add grab cursor for drag scrolling
          this.scrollableTrack.classList.remove("cursor-default");
          this.scrollableTrack.classList.add("cursor-grab");
          // Re-enable drag scrolling
          this.addDragEventListeners();
        } else {
          // Disable scrolling - styles are handled by Liquid template
          // Remove grab cursor and add default cursor
          this.scrollableTrack.classList.remove("cursor-grab", "cursor-grabbing");
          this.scrollableTrack.classList.add("cursor-default");
          // Disable drag scrolling
          this.removeDragEventListeners();
          this.dragScrollingDisabled = true;
        }
      }

      // Update pagination and arrows visibility based on current scroll state
      this.updatePaginationVisibility();
      this.updateArrowsVisibility();

      // Force a reflow to ensure styles are applied
      if (this.scrollableTrack) {
        this.scrollableTrack.offsetHeight;
      }
    }

    cleanup() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      // Clean up media query listener
      if (this.xlMediaQuery) {
        this.xlMediaQuery.removeEventListener("change", this.handleBreakpointChange.bind(this));
      }

      // Clean up drag event listeners
      this.removeDragEventListeners();
    }
  }

  customElements.define("scrollable-group", ScrollableGroup);
}